import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  algoliaSearch,
  AlgoliaSearchParams,
} from "../../../api/postCall/algoliaSearch";
import { AlgoliaSearchResponse } from "../../../data-model/algoliaSearchModel";

interface MedicalPaOrdersAlgoliaState {
  data?: AlgoliaSearchResponse;
  loading?: boolean;
  error?: string;
  showinLineLoader?: boolean;
}

const initialState: MedicalPaOrdersAlgoliaState = {
  data: undefined,
  loading: false,
  error: undefined,
  showinLineLoader: false,
};

const medicalPaOrdersAlgoliaSlice = createSlice({
  name: "medicalPaOrdersAlgolia",
  initialState,
  reducers: {
    setMedicalPaOrdersAlgoliaData: (
      state,
      action: PayloadAction<AlgoliaSearchResponse | undefined>,
    ) => {
      state.data = action.payload;
      state.loading = false;
      state.error = undefined;
      state.showinLineLoader = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      state.data = undefined;
      state.error = undefined;
      state.showinLineLoader = false;
    },
    setError: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload;
      state.loading = false;
      state.data = undefined;
      state.showinLineLoader = false;
    },
    resetMedicalPaOrdersAlgoliaData: (state) => {
      state.data = undefined;
      state.loading = false;
      state.error = undefined;
      state.showinLineLoader = false;
    },
    setinLineLoader: (state, action: PayloadAction<boolean>) => {
      state.showinLineLoader = action.payload;
    },
    prependData: (state, action: PayloadAction<AlgoliaSearchResponse>) => {
      state.data = state.data
        ? {
            ...action.payload,
            hits: [
              ...(action.payload.hits ?? []),
              ...(state?.data?.hits ?? []),
            ],
          }
        : action.payload;
      state.loading = false;
      state.error = undefined;
      state.showinLineLoader = false;
    },
    appendData: (state, action: PayloadAction<AlgoliaSearchResponse>) => {
      state.data = state.data
        ? {
            ...action.payload,
            hits: [
              ...(state?.data?.hits ?? []),
              ...(action.payload.hits ?? []),
            ],
          }
        : action.payload;
      state.error = undefined;
      state.showinLineLoader = false;
    },
    setLoadingFalse: (state) => {
      state.loading = false;
    },
  },
});

export const {
  setMedicalPaOrdersAlgoliaData,
  setLoading,
  setError,
  resetMedicalPaOrdersAlgoliaData,
  setinLineLoader,
  prependData,
  appendData,
  setLoadingFalse,
} = medicalPaOrdersAlgoliaSlice.actions;

export const fetchMedicalPaOrdersAlgoliaData =
  (payload: AlgoliaSearchParams) => async (dispatch, getState: any) => {
    const { medicalPaOrdersAlgolia } = getState();
    if (medicalPaOrdersAlgolia.loading) {
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await algoliaSearch(payload);
      dispatch(
        setMedicalPaOrdersAlgoliaData(
          JSON.parse(response) as AlgoliaSearchResponse,
        ),
      );
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

export const fetchMoreMedicalPaOrdersAlgoliaData =
  (payload: AlgoliaSearchParams) => async (dispatch, getState: any) => {
    const { medicalPaOrdersAlgolia } = getState();
    if (medicalPaOrdersAlgolia.loading) {
      return;
    }
    dispatch(setinLineLoader(true));
    try {
      const response = await algoliaSearch(payload);
      dispatch(appendData(JSON.parse(response) as AlgoliaSearchResponse));
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

export const fetchPreviousMedicalPaOrdersAlgoliaData =
  (payload: AlgoliaSearchParams) => async (dispatch, getState: any) => {
    const { medicalPaOrdersAlgolia } = getState();
    if (medicalPaOrdersAlgolia.loading) {
      return;
    }
    dispatch(setinLineLoader(true));
    try {
      const response = await algoliaSearch(payload);
      dispatch(prependData(JSON.parse(response) as AlgoliaSearchResponse));
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

export const fetchDataUptoGivenPageNumberFromMedicalPaOrdersAlgolia =
  (payloads: AlgoliaSearchParams[]) => async (dispatch, getState: any) => {
    const { medicalPaOrdersAlgolia } = getState();
    if (medicalPaOrdersAlgolia.loading) {
      return;
    }
    dispatch(setLoading(true));
    try {
      for (const params of payloads) {
        const response = await algoliaSearch(params);
        const parsed = JSON.parse(response) as AlgoliaSearchResponse;
        dispatch(appendData(parsed));
      }
    } catch (error) {
      dispatch(setError(error as string));
    } finally {
      dispatch(setLoadingFalse());
    }
  };

export default medicalPaOrdersAlgoliaSlice.reducer;
